// A type-ahead lookup: text is asked about after a typing pause, so a word
// typed fast is one request; only the latest text's answer is delivered (an
// older one landing late is dropped); and `state.slow` turns on once the
// lookup in flight has taken longer than `slowMs`, so a slow answer is never
// silent. The want page's Add a card row is its user.

/**
 * @template T
 * @param {(text: string) => Promise<T>} fetchFn
 * @param {{ pauseMs: number, slowMs: number, onResult: (result: T) => void }} opts
 */
export function createTypeahead(fetchFn, { pauseMs, slowMs, onResult }) {
  const state = $state({ slow: false });
  // The latest lookup; an answer for an older one is dropped.
  let seq = 0;
  // A lookup waiting on the typing pause, and the slow mark's (0: none).
  let pauseTimer = 0;
  let slowTimer = 0;
  /** @type {Promise<void> | null} the latest lookup, until it answers */
  let inflight = null;

  function clearPause() {
    clearTimeout(pauseTimer);
    pauseTimer = 0;
  }

  function clearSlow() {
    clearTimeout(slowTimer);
    slowTimer = 0;
    state.slow = false;
  }

  /** @param {string} text */
  function ask(text) {
    const mine = ++seq;
    clearSlow();
    slowTimer = window.setTimeout(() => (state.slow = true), slowMs);
    const run = fetchFn(text).then((result) => {
      if (mine !== seq) return;
      inflight = null;
      clearSlow();
      onResult(result);
    });
    inflight = run;
    return run;
  }

  return {
    state,

    /** Ask about `text` after the typing pause (a newer text restarts it).
     * @param {string} text */
    schedule(text) {
      clearPause();
      pauseTimer = window.setTimeout(() => {
        pauseTimer = 0;
        ask(text);
      }, pauseMs);
    },

    /**
     * The answer for `text`, the text as it stands: a lookup still waiting
     * on the pause is asked now, one in flight is awaited; null when there
     * is nothing to wait for.
     * @param {string} text
     * @returns {Promise<void> | null}
     */
    settle(text) {
      if (!pauseTimer) return inflight;
      clearPause();
      return ask(text);
    },

    /** Nothing pending is asked or delivered any more. */
    cancel() {
      clearPause();
      clearSlow();
      seq += 1;
      inflight = null;
    },
  };
}
